import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export function usePermissions() {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<any[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [orgRole, setOrgRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setPermissions([]);
      setRoles([]);
      setOrgRole(null);
      setLoading(false);
      return;
    }

    fetchPermissions();
  }, [user]);

  const fetchPermissions = async () => {
    if (!user) return;

    console.log('usePermissions: fetchPermissions called for user:', user.id);
    try {
      // Fetch user roles
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      const userRoles = rolesData?.map(r => r.role) || [];
      console.log('usePermissions: user roles fetched:', userRoles);
      setRoles(userRoles);

      // Fetch organization role - prioritize 'owner', then 'admin', then 'member'
      const { data: orgData } = await supabase
        .from('org_memberships')
        .select('role')
        .eq('user_id', user.id)
        .eq('active', true)
        .order('role', { ascending: false }) // This will prioritize owner > member > admin alphabetically
        .limit(1)
        .maybeSingle();

      let organizationRole = null;
      if (orgData) {
        organizationRole = orgData.role;
      }

      console.log('usePermissions: organization role fetched:', organizationRole);
      setOrgRole(organizationRole);

      // Fetch user permissions
      const { data: permissionsData } = await supabase
        .from('permissions')
        .select('*')
        .eq('user_id', user.id);

      console.log('usePermissions: user permissions fetched:', permissionsData);
      setPermissions(permissionsData || []);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      console.log('usePermissions: setLoading(false)');
      setLoading(false);
    }
  };

  const hasRole = (role: string) => {
    const result = roles.includes(role);
    console.log(`usePermissions: hasRole('${role}') = ${result}`, 'roles:', roles);
    return result;
  };

  const isSuperAdmin = () => {
    const result = roles.includes('super_admin');
    console.log(`usePermissions: isSuperAdmin() = ${result}`, 'roles:', roles);
    return result;
  };

  const hasPermission = (module: string, submodule?: string, permissionType: 'read' | 'write' | 'admin' = 'read') => {
    console.log(`usePermissions: hasPermission called with module='${module}', submodule='${submodule}', permissionType='${permissionType}'`);
    console.log('usePermissions: current roles:', roles);
    console.log('usePermissions: current organization role:', orgRole);
    console.log('usePermissions: current permissions:', permissions);
    
    // Super admins have access to everything
    if (isSuperAdmin()) {
      console.log('usePermissions: User is super admin, returning true');
      return true;
    }
    
    // Regular admins have access to everything
    if (hasRole('admin')) {
      console.log('usePermissions: User is admin, returning true');
      return true;
    }

    // Organization owners and admins have full CRUD access to everything except "organizacoes"
    if (orgRole === 'owner' || orgRole === 'admin') {
      console.log('usePermissions: User is organization owner/admin');
      // Only restrict access to organizacoes for regular org admins (not super admins)
      if (module === 'administracao' && submodule === 'organizacoes' && !isSuperAdmin()) {
        // Org admins can only read their own organization, not manage all organizations
        return permissionType === 'read';
      }
      return true;
    }

    // Organization members have full CRUD access to all modules except organizacoes  
    if (orgRole === 'member') {
      console.log('usePermissions: User is organization member');
      // Members cannot access organizacoes at all
      if (module === 'administracao' && submodule === 'organizacoes') {
        return false;
      }
      // Full access to everything else
      return true;
    }

    // Check specific permission for users without org roles
    const result = permissions.some(p => 
      p.module === module && 
      (!submodule || p.submodule === submodule) &&
      p.permission_type === permissionType
    );
    
    console.log(`usePermissions: Specific permission check result: ${result}`);
    return result;
  };


  const requestAccess = async (module: string, submodule?: string, permissionType: 'read' | 'write' | 'admin' = 'read', reason?: string) => {
    if (!user) return { error: 'Usuário não autenticado' };

    try {
      const { error } = await supabase
        .from('access_requests')
        .insert([{
          requester_id: user.id,
          module,
          submodule,
          permission_type: permissionType,
          reason
        }]);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      return { error: error.message };
    }
  };

  return {
    permissions,
    roles,
    orgRole,
    loading,
    hasRole,
    hasPermission,
    isSuperAdmin,
    requestAccess,
    refetch: fetchPermissions
  };
}